/**
 * Template Layout Card Component
 * Displays a template slide with Screenshot/Preview toggle
 * - Screenshot view: Shows slide_screenshot_cropped.png (available after validation)
 * - Preview view: Shows interactive GSAP slide.json (available after layout generation)
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Card } from '../ui/card';
import { cn } from '../../lib/utils';
import { GSAP_VIEWER_CONFIG } from '../../config/gsapViewer';
import { slidesAPI, templateAPI } from '../../services/api';
import { useAgentJob } from '../../hooks/useAgentJob';

export interface TemplateLayoutSlide {
  slideId: string;
  slideNumber: number;
  pngPath: string;
  pngGcsUrl?: string;
  slideJsonPath?: string;
  slideJsonGcsUrl?: string;
  htmlPath: string;
  metadataPath?: string;
  hasGsapSlide: boolean;
  assets: string[];
}

interface TemplateLayoutCardProps {
  slide: TemplateLayoutSlide;
  conversationId: string;
  onSlideClick?: (slide: TemplateLayoutSlide) => void;
  index?: number;
}

export const TemplateLayoutCard: React.FC<TemplateLayoutCardProps> = ({
  slide,
  conversationId,
  onSlideClick,
  index = 0,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.15);
  const [shouldLoadIframe, setShouldLoadIframe] = useState(false);
  const [viewMode, setViewMode] = useState<'screenshot' | 'preview'>('screenshot');
  const [iframeError, setIframeError] = useState<string | null>(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);

  // Job polling for layout generation
  const {
    job,
    error: jobErrorState,
  } = useAgentJob(activeJobId, {
    pollInterval: 2000,
    onComplete: (result) => {
      console.log(`Layout generation completed for slide ${slide.slideId}:`, result);
      const storageKey = `layout_generation_${conversationId}_${slide.slideId}`;
      localStorage.removeItem(storageKey);
      setActiveJobId(null);
      // Parent component should refresh to show updated hasGsapSlide status
      // Trigger a page refresh or emit event
      window.location.reload(); // Simple approach for now
    },
    onError: (error) => {
      console.error(`Layout generation failed for slide ${slide.slideId}:`, error);
      const storageKey = `layout_generation_${conversationId}_${slide.slideId}`;
      localStorage.removeItem(storageKey);
    },
  });

  // Extract job status and progress from job object
  const jobStatus = job?.status;
  const jobProgress = job?.progress?.thinking || job?.progress?.currentOperation;
  const jobError = job?.error || jobErrorState;

  // Check localStorage for existing job on mount
  useEffect(() => {
    const storageKey = `layout_generation_${conversationId}_${slide.slideId}`;
    const existingJobId = localStorage.getItem(storageKey);
    if (existingJobId && !slide.hasGsapSlide && !activeJobId) {
      console.log(`Resuming layout generation job ${existingJobId} for slide ${slide.slideId}`);
      setActiveJobId(existingJobId);
    }
  }, [conversationId, slide.slideId, slide.hasGsapSlide, activeJobId]);

  // Handle Generate button click
  const handleGenerate = useCallback(async () => {
    try {
      console.log(`Starting layout generation for slide ${slide.slideId}`);
      const response = await templateAPI.startLayoutGeneration(conversationId, slide.slideId);

      if (response.success && response.jobId) {
        // Store job ID in localStorage for persistence
        const storageKey = `layout_generation_${conversationId}_${slide.slideId}`;
        localStorage.setItem(storageKey, response.jobId);

        // Set job ID to start polling
        setActiveJobId(response.jobId);
        console.log(`Layout generation job started: ${response.jobId}`);
      }
    } catch (error) {
      console.error('Failed to start layout generation:', error);
    }
  }, [conversationId, slide.slideId]);

  // Handle Edit button click
  const handleEdit = useCallback(() => {
    if (slide.hasGsapSlide) {
      console.log(`Opening editor for slide ${slide.slideId}`);
      // Navigate to slide editor (similar to video workflow)
      // TODO: Implement slide editor navigation for template slides
      alert('Edit functionality coming soon!');
    }
  }, [slide.hasGsapSlide, slide.slideId]);

  // Lazy load iframe based on visibility
  useEffect(() => {
    // Only load iframe if preview mode is available
    if (!slide.hasGsapSlide) return;

    // Load immediately if in first 3 slides
    if (index < 3) {
      setShouldLoadIframe(true);
      return;
    }

    // Set up intersection observer for lazy loading
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !shouldLoadIframe) {
            console.log(`TemplateLayoutCard - Lazy loading iframe for slide ${index}`);
            setShouldLoadIframe(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: '50px',
        threshold: 0.01,
      }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, [index, shouldLoadIframe, slide.hasGsapSlide]);

  // Listen for postMessage errors from iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Only accept messages from our iframe
      if (event.data && event.data.type === 'viewer-error') {
        console.error(`TemplateLayoutCard - Iframe error for slide ${slide.slideNumber}:`, event.data.error);
        setIframeError(event.data.error);
      } else if (event.data && event.data.type === 'viewer-loaded') {
        console.log(`TemplateLayoutCard - Iframe loaded successfully for slide ${slide.slideNumber}`);
        setIframeLoaded(true);
        setIframeError(null);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [slide.slideNumber]);

  // Scale calculation effect
  useEffect(() => {
    const updateScale = () => {
      if (containerRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect();
        const containerWidth = containerRect.width;
        const containerHeight = containerRect.height;

        const scaleX = containerWidth / GSAP_VIEWER_CONFIG.slideResolution.width;
        const scaleY = containerHeight / GSAP_VIEWER_CONFIG.slideResolution.height;
        const optimalScale = Math.min(scaleX, scaleY);

        setScale(optimalScale);
      }
    };

    updateScale();
    window.addEventListener('resize', updateScale);

    if (containerRef.current) {
      const resizeObserver = new ResizeObserver(updateScale);
      resizeObserver.observe(containerRef.current);

      return () => {
        window.removeEventListener('resize', updateScale);
        resizeObserver.disconnect();
      };
    }

    return () => window.removeEventListener('resize', updateScale);
  }, []);

  const handlePreviewClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onSlideClick) {
      onSlideClick(slide);
    }
  };

  // Extract relative path from absolute slide path for GSAP viewer
  const extractRelativePath = (absolutePath: string | undefined): string => {
    // Handle undefined or empty path
    if (!absolutePath) {
      return 'slide/slide.json'; // Default path
    }

    // Check if it's an absolute path (starts with /)
    if (absolutePath.startsWith('/')) {
      // Absolute path - need to extract relative part
      // For template slides: /path/slide_01/slide.json → slide_01/slide.json
      const templateMatch = absolutePath.match(/\/(slide_\d+\/[^/]+)$/);
      if (templateMatch) {
        return templateMatch[1];
      }

      // For video slides with absolute paths: /path/slides/slide-002/slide.json → slides/slide-002/slide.json
      const videoMatch = absolutePath.match(/\/(slides\/slide[^/]+\/[^/]+)$/);
      if (videoMatch) {
        return videoMatch[1];
      }

      // Fallback: return last 2 parts (slide_01/slide.json)
      return absolutePath.split('/').slice(-2).join('/');
    }

    // If it's already relative (video slides), use as-is
    return absolutePath;
  };

  // Build viewer URL for GSAP slide
  let viewerUrl = '';
  if (slide.slideJsonPath) {
    const relativePath = extractRelativePath(slide.slideJsonPath);
    const jsonApiUrl = slidesAPI.getJSONUrl(conversationId, relativePath);
    viewerUrl = GSAP_VIEWER_CONFIG.buildUrl(jsonApiUrl, {
      editMode: false,
      controls: false,
      uiMode: 'external',
      conversationId,
    });
  }

  // Build PNG URL - prefer GCS URL, fallback to filesystem API
  const pngUrl = slide.pngGcsUrl || `/api/filesystem/read?path=${encodeURIComponent(slide.pngPath)}`;

  // Handle iframe load
  const handleIframeLoad = () => {
    console.log(`✅ TemplateLayoutCard - Iframe loaded for slide ${slide.slideNumber}`);
    setIframeLoaded(true);
    setIframeError(null);
  };

  // Handle iframe error
  const handleIframeError = (error: any) => {
    console.error(`❌ TemplateLayoutCard - Iframe error for slide ${slide.slideNumber}:`, error);
    setIframeError('Failed to load slide viewer');
  };

  return (
    <Card
      ref={cardRef}
      className="group transition-all duration-300 hover:shadow-xl bg-card border border-border/60 hover:border-primary/50 relative overflow-hidden h-[380px] flex flex-col rounded-xl backdrop-blur-sm"
    >
      {/* Toggle Bar */}
      <div className="flex-shrink-0 border-b bg-muted/30 overflow-hidden">
        <div className="flex">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setViewMode('screenshot');
            }}
            className={cn(
              'flex-1 py-2 px-3 text-xs font-medium transition-all flex items-center justify-center gap-1',
              viewMode === 'screenshot'
                ? 'bg-background shadow-sm text-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
            )}
          >
            <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <span>Screenshot</span>
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (slide.hasGsapSlide) {
                setViewMode('preview');
              }
            }}
            disabled={!slide.hasGsapSlide}
            className={cn(
              'flex-1 py-2 px-3 text-xs font-medium transition-all flex items-center justify-center gap-1',
              viewMode === 'preview'
                ? 'bg-background shadow-sm text-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/50',
              !slide.hasGsapSlide && 'opacity-50 cursor-not-allowed'
            )}
            title={!slide.hasGsapSlide ? 'Preview will be available after layout generation' : ''}
          >
            <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
              />
            </svg>
            <span>Preview</span>
          </button>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-hidden relative">
        {viewMode === 'screenshot' ? (
          /* Screenshot Content */
          <div className="h-full flex flex-col p-4 relative">
            {/* Header */}
            <div className="mb-3 flex-shrink-0">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-primary">Layout {slide.slideNumber}</span>
                <h3 className="font-semibold text-sm text-foreground flex-1 truncate">
                  {slide.slideId}
                </h3>
              </div>
            </div>

            {/* Screenshot Display */}
            <div
              className="relative flex-1 cursor-pointer rounded-lg overflow-hidden bg-gradient-to-br from-muted/20 to-muted/5"
              onClick={handlePreviewClick}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              title="Click to view full screen"
            >
              <div className="h-full overflow-hidden relative flex items-center justify-center">
                {!imageLoaded && (
                  <div className="absolute inset-0 flex items-center justify-center bg-muted/10">
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                      <span className="text-xs text-muted-foreground">Loading screenshot...</span>
                    </div>
                  </div>
                )}
                <img
                  src={pngUrl}
                  alt={`Layout ${slide.slideNumber} screenshot`}
                  className="w-full h-full object-contain"
                  onLoad={() => setImageLoaded(true)}
                  onError={() => {
                    console.error(`Failed to load screenshot for slide ${slide.slideNumber}`);
                    setImageLoaded(true);
                  }}
                />

                {/* Hover overlay */}
                {imageLoaded && isHovered && (
                  <div className="absolute inset-0 bg-black/0 hover:bg-black/10 transition-all duration-300 flex items-center justify-center">
                    <div className="bg-white/95 backdrop-blur-md rounded-full p-3 shadow-lg">
                      <svg
                        className="w-6 h-6 text-gray-700"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                        />
                      </svg>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons and Status */}
            <div className="flex-shrink-0 flex flex-col gap-2 pt-3 border-t border-border/30 mt-3">
              {/* Agent thinking animation */}
              {(jobStatus === 'active' || jobStatus === 'pending') && (
                <div className="bg-accent border border-primary/30 rounded-lg p-2">
                  <div className="flex items-center gap-2">
                    <div className="flex-shrink-0">
                      <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-accent-foreground">Generating layout...</div>
                      {jobProgress && (
                        <div className="text-xs text-accent-foreground truncate">{jobProgress}</div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Error display */}
              {jobStatus === 'failed' && jobError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-2">
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="text-xs text-red-900 flex-1 min-w-0 truncate">{jobError}</div>
                  </div>
                </div>
              )}

              {/* Action buttons and info */}
              <div className="flex items-center justify-between gap-2">
                <div className="text-xs text-muted-foreground">
                  {slide.assets.length} {slide.assets.length === 1 ? 'asset' : 'assets'}
                </div>
                <div className="flex items-center gap-2">
                  {!slide.hasGsapSlide && jobStatus !== 'active' && jobStatus !== 'pending' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleGenerate();
                      }}
                      className="text-xs px-3 py-1 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors font-medium"
                    >
                      Generate
                    </button>
                  )}
                  {slide.hasGsapSlide && (
                    <>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit();
                        }}
                        className="text-xs px-3 py-1 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 transition-colors font-medium"
                      >
                        Edit
                      </button>
                      <div className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                        GSAP ready
                      </div>
                    </>
                  )}
                  {!slide.hasGsapSlide && jobStatus !== 'active' && jobStatus !== 'pending' && (
                    <div className="text-xs text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full">
                      Pending
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Preview Content - GSAP Slide */
          <div className="h-full flex flex-col p-4 relative">
            {/* Header */}
            <div className="mb-3 flex-shrink-0">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-primary">Layout {slide.slideNumber}</span>
                <h3 className="font-semibold text-sm text-foreground flex-1 truncate">
                  {slide.slideId}
                </h3>
              </div>
            </div>

            {/* GSAP Preview Section */}
            <div
              className="relative flex-1 cursor-pointer rounded-lg overflow-hidden bg-gradient-to-br from-muted/20 to-muted/5"
              onClick={handlePreviewClick}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              title="Click to view full screen"
            >
              <div
                ref={containerRef}
                className="h-full overflow-hidden relative flex items-center justify-center"
              >
                {iframeError ? (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-red-50/50 p-4">
                    <svg className="w-8 h-8 text-red-500 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-xs text-red-700 text-center">{iframeError}</span>
                  </div>
                ) : shouldLoadIframe ? (
                  <>
                    <iframe
                      key={`gsap-iframe-${slide.slideId}`}
                      ref={iframeRef}
                      src={viewerUrl}
                      className="absolute border-0 pointer-events-none"
                      title={slide.slideId}
                      sandbox="allow-same-origin allow-scripts allow-forms allow-modals"
                      onLoad={handleIframeLoad}
                      onError={handleIframeError}
                      style={{
                        width: `${GSAP_VIEWER_CONFIG.slideResolution.width}px`,
                        height: `${GSAP_VIEWER_CONFIG.slideResolution.height}px`,
                        transform: `scale(${scale})`,
                        transformOrigin: 'top left',
                        left: '0',
                        top: '0',
                      }}
                    />
                    {!iframeLoaded && (
                      <div className="absolute inset-0 flex items-center justify-center bg-muted/10 pointer-events-none">
                        <div className="flex flex-col items-center gap-2">
                          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                          <span className="text-xs text-muted-foreground">Loading slide...</span>
                        </div>
                      </div>
                    )}
                    {/* Hover overlay */}
                    {iframeLoaded && isHovered && (
                      <div className="absolute inset-0 bg-black/0 hover:bg-black/10 transition-all duration-300 flex items-center justify-center">
                        <div className="bg-white/95 backdrop-blur-md rounded-full p-3 shadow-lg">
                          <svg
                            className="w-6 h-6 text-gray-700"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                            />
                          </svg>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center bg-muted/10">
                    <span className="text-xs text-muted-foreground">Loading...</span>
                  </div>
                )}
              </div>
            </div>

            {/* Bottom Info and Actions */}
            <div className="flex-shrink-0 flex items-center justify-between gap-2 pt-3 border-t border-border/30 mt-3">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
                <span>GSAP Animation</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEdit();
                  }}
                  className="text-xs px-3 py-1 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 transition-colors font-medium"
                >
                  Edit
                </button>
                <div className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                  Interactive
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};
